/*
 * <image-compare> — a before/after wipe between two stacked layers.
 *
 * Markup (see snippets/course-overview-slide.liquid, sections/course-compare.liquid):
 *   <image-compare data-touch="anywhere|handle">
 *     .image-compare__under   the layer revealed on the left (satellite photo)
 *     .image-compare__over    the layer wiped over it, shown right of the handle
 *     .image-compare__handle  > .image-compare__knob
 *     input.image-compare__range   keyboard + screen-reader control
 *   </image-compare>
 *
 * The layers are expected to be the same rectangle (the API cuts the satellite
 * image to the design's own canvas), so all this does is move one clip edge.
 *
 * Touch: `data-touch="anywhere"` lets a horizontal drag start anywhere on the
 * element (vertical swipes still scroll the page). `data-touch="handle"` only
 * listens on the knob — used inside the product gallery, whose own horizontal
 * swipe must keep working everywhere else on the slide.
 *
 * Mouse: a drag moves the edge; a plain click is left alone, so the product
 * gallery's click-to-zoom (composite-zoom.js) still works on the same slide.
 */
if (!customElements.get('image-compare')) {
  customElements.define(
    'image-compare',
    class ImageCompare extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        this.range = this.querySelector('.image-compare__range');
        this.handle = this.querySelector('.image-compare__handle');
        this.set(Number(this.dataset.start) || 50);

        this.addEventListener('pointerdown', this.onDown.bind(this));
        this.addEventListener('pointermove', this.onMove.bind(this));
        this.addEventListener('pointerup', this.onUp.bind(this));
        this.addEventListener('pointercancel', this.onUp.bind(this));
        // A drag ends in a click; swallow it so it doesn't toggle zoom.
        this.addEventListener('click', (e) => {
          if (!this._dragged) return;
          this._dragged = false;
          e.stopPropagation();
          e.preventDefault();
        });
        if (this.range) {
          this.range.addEventListener('input', () => this.set(Number(this.range.value)));
        }
      }

      set(pct) {
        const v = Math.min(100, Math.max(0, pct));
        this.pos = v;
        this.style.setProperty('--compare-pos', v + '%');
        this.classList.toggle('is-at-start', v < 8);
        this.classList.toggle('is-at-end', v > 92);
        if (this.range && Number(this.range.value) !== Math.round(v)) this.range.value = Math.round(v);
      }

      setFromClientX(x) {
        const r = this.getBoundingClientRect();
        if (!r.width) return;
        this.set(((x - r.left) / r.width) * 100);
      }

      // Inactive until the layer underneath has arrived (`is-ready`); a product
      // slide shows the plain design until then.
      active() {
        return !this.hasAttribute('data-requires-ready') || this.classList.contains('is-ready');
      }

      onDown(e) {
        this._dragged = false;
        if (!this.active() || (e.pointerType === 'mouse' && e.button !== 0)) return;
        const onHandle = this.handle && this.handle.contains(e.target);
        if (e.pointerType !== 'mouse' && this.dataset.touch === 'handle' && !onHandle) return;
        this._dragging = true;
        this._grabbed = onHandle;
        this._startX = e.clientX;
        try {
          this.setPointerCapture(e.pointerId);
        } catch (err) {
          /* capture is a nicety; dragging still works inside the element */
        }
      }

      onMove(e) {
        if (!this._dragging) return;
        // A few pixels of slack so a click with a shaky hand is still a click.
        if (!this._dragged && !this._grabbed && Math.abs(e.clientX - this._startX) < 4) return;
        this._dragged = true;
        this.classList.add('is-dragging');
        this.setFromClientX(e.clientX);
      }

      onUp(e) {
        if (!this._dragging) return;
        this._dragging = false;
        this.classList.remove('is-dragging');
        try {
          this.releasePointerCapture(e.pointerId);
        } catch (err) {
          /* already released */
        }
      }
    }
  );
}
